import AppLogo from "@/components/app-logo";
import { Separator } from "@/components/ui/separator";

interface FooterLink {
  href: string;
  label: string;
  slug: string;
}

interface SiteFooterProps {
  className?: string;
  links?: FooterLink[];
  showSeparator?: boolean;
}

interface FooterSectionDefinition {
  title: string;
  slugs: string[];
}

const defaultLinks: FooterLink[] = [
  { href: "/", label: "Home", slug: "home" },
  { href: "/sign_in", label: "Log in", slug: "sign_in" },
  { href: "/#pricing", label: "Pricing", slug: "pricing" },
  { href: "/new", label: "Create", slug: "new" },
  { href: "/agencies", label: "For Agencies", slug: "agencies" },
  { href: "/gallery", label: "The OG Pilot Gallery", slug: "gallery" },
  { href: "/docs", label: "API Docs", slug: "docs" },
  { href: "/docs/mcp", label: "MCP Connector", slug: "docs-mcp" },
  {
    href: "/case-studies/nike-store",
    label: "Nike ecommerce makeover",
    slug: "case-study-nike",
  },
  {
    href: "/case-studies/brooklyn-botanic-garden",
    label: "Brooklyn Botanic Garden launch",
    slug: "case-study-bbg",
  },
  {
    href: "/case-studies/office-ladies-podcast",
    label: "Office Ladies podcast boost",
    slug: "case-study-office-ladies",
  },
  {
    href: "/open-graph-debugger",
    label: "Open Graph Debugger",
    slug: "open-graph-debugger",
  },
  {
    href: "/image-color-palette-extractor",
    label: "Color Palette Extractor",
    slug: "image-color-palette-extractor",
  },
  {
    href: "/image-compressor",
    label: "Image Compressor",
    slug: "image-compressor",
  },
  {
    href: "/carrd-social-image",
    label: "Carrd Social Image",
    slug: "carrd-social-image",
  },
  {
    href: "/carrd-og-image",
    label: "Carrd OG Image",
    slug: "carrd-og-image",
  },
  {
    href: "/wordpress",
    label: "WordPress OG Guide",
    slug: "wordpress",
  },
  {
    href: "/change-og-image-wordpress",
    label: "Change OG Image in WordPress",
    slug: "change-og-image-wordpress",
  },
  {
    href: "/open-graph-protocol-wordpress",
    label: "Open Graph Protocol on WordPress",
    slug: "open-graph-protocol-wordpress",
  },
  {
    href: "/og-image-api",
    label: "OG Image API",
    slug: "og-image-api",
  },
  {
    href: "/og-image-size",
    label: "OG Image Size Cheat Sheet",
    slug: "og-image-size",
  },
  {
    href: "/bannerbear-alternative",
    label: "Bannerbear Alternative",
    slug: "bannerbear-alternative",
  },
  {
    href: "/rails-og-image",
    label: "Rails OG Image",
    slug: "rails-og-image",
  },
  {
    href: "/nextjs-og-image-generator",
    label: "Next.js OG Image Guide",
    slug: "nextjs-og-image-generator",
  },
  {
    href: "/rails-og-image-generator",
    label: "Rails OG Image Guide",
    slug: "rails-og-image-generator",
  },
  {
    href: "/astro-og-image-generator",
    label: "Astro OG Image Guide",
    slug: "astro-og-image-generator",
  },
  {
    href: "/astro-og-image",
    label: "Astro OG Image Comparison",
    slug: "astro-og-image",
  },
  {
    href: "mailto:support@ogpilot.com",
    label: "support@ogpilot.com",
    slug: "support",
  },
  { href: "/privacy", label: "Privacy Policy", slug: "privacy" },
  { href: "/terms", label: "Terms of Service", slug: "terms" },
];

const sectionDefinitions: FooterSectionDefinition[] = [
  {
    title: "Product",
    slugs: ["home", "new", "sign_in", "pricing", "agencies", "gallery"],
  },
  {
    title: "Case Studies",
    slugs: ["case-study-nike", "case-study-bbg", "case-study-office-ladies"],
  },
  {
    title: "Free Tools",
    slugs: ["open-graph-debugger", "image-color-palette-extractor", "image-compressor"],
  },
  {
    title: "Company",
    slugs: ["privacy", "terms", "support"],
  },
  {
    title: "Resources",
    slugs: [
      "docs",
      "docs-mcp",
      "carrd-social-image",
      "carrd-og-image",
      "wordpress",
      "change-og-image-wordpress",
      "open-graph-protocol-wordpress",
      "og-image-api",
      "og-image-size",
      "bannerbear-alternative",
      "rails-og-image",
      "nextjs-og-image-generator",
      "rails-og-image-generator",
      "astro-og-image-generator",
      "astro-og-image",
    ],
  },
];

export function SiteFooter({ className, links = defaultLinks, showSeparator = true }: SiteFooterProps) {
  const year = new Date().getFullYear();

  const linkLookup = new Map(links.map((link) => [link.slug, link]));
  const sections = sectionDefinitions
    .map((section) => ({
      title: section.title,
      links: section.slugs.map((slug) => linkLookup.get(slug)).filter((link): link is FooterLink => Boolean(link)),
    }))
    .filter((section) => section.links.length > 0);

  const productSectionIndex = sections.findIndex((section) => section.title === "Product");
  const productLinks = productSectionIndex >= 0 ? sections[productSectionIndex].links : [];
  const filteredSections =
    productSectionIndex >= 0 ? sections.filter((section) => section.title !== "Product") : sections;

  const renderedSlugs = new Set([
    ...filteredSections.flatMap((section) => section.links.map((link) => link.slug)),
    ...productLinks.map((link) => link.slug),
  ]);
  const additionalLinks = links.filter((link) => !renderedSlugs.has(link.slug));

  return (
    <footer className={className}>
      {showSeparator && <Separator className="mb-12" />}
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-12 px-4 py-8">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,2fr)_repeat(4,minmax(0,1fr))]">
          <div className="flex flex-col gap-6">
            <AppLogo />
            <p className="text-muted-foreground max-w-sm text-sm">
              Automated social image generation for modern teams. Create professional branded images in seconds and
              increase your click-through rates by 3x.
            </p>
            <p className="text-muted-foreground text-sm">All rights reserved.</p>
            {productLinks.length > 0 && (
              <div className="text-muted-foreground text-sm">
                <h3 className="mb-2 text-sm font-semibold tracking-wide uppercase">Product</h3>
                <ul className="space-y-2">
                  {productLinks.map((link) => (
                    <li key={link.slug}>
                      <a
                        href={link.href}
                        data-fast-goal="footer_link_click"
                        data-fast-goal-link={link.slug}
                        className="hover:text-foreground transition-colors"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {filteredSections.map((section) => (
            <div key={section.title} className="space-y-4">
              <h3 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">{section.title}</h3>
              <ul className="text-muted-foreground space-y-2 text-sm">
                {section.links.map((link) => (
                  <li key={link.slug}>
                    <a
                      href={link.href}
                      data-fast-goal="footer_link_click"
                      data-fast-goal-link={link.slug}
                      className="hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {additionalLinks.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">More</h3>
              <ul className="text-muted-foreground space-y-2 text-sm">
                {additionalLinks.map((link) => (
                  <li key={link.slug}>
                    <a
                      href={link.href}
                      data-fast-goal="footer_link_click"
                      data-fast-goal-link={link.slug}
                      className="hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="border-border text-muted-foreground flex flex-col gap-4 border-t pt-6 text-xs sm:flex-row sm:items-center sm:justify-between">
          <p>© {year} OG Pilot. The dynamic Open Graph image generator.</p>
          <p>Professional social images, on autopilot.</p>
        </div>
      </div>
    </footer>
  );
}

export type { FooterLink, SiteFooterProps };
